import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { API } from '../utils/constants';
import { setupSubscriptionPayment } from '../utils/subscriptionCheckout';
import { Check, ArrowLeft, Link } from "lucide-react";

const stripePromise = axios.get(`${API}/subscription/config`).then(res => {
  const key = res.data.publishable_key;
  if (!key) return null;
  return loadStripe(key);
});

const CheckoutForm = ({ isYearly, onStatusChange }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  React.useEffect(() => {
    if (onStatusChange) {
      onStatusChange({ stripe, elements, isProcessing });
    }
  }, [stripe, elements, isProcessing]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMessage(null);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/recipes?payment=success`,
      },
    });

    if (error) {
      setErrorMessage(error.message);
      setIsProcessing(false);
    }
  };

  React.useEffect(() => {
    window.__checkoutSubmit = handleSubmit;
    return () => { delete window.__checkoutSubmit; };
  });

  return (
    <form onSubmit={handleSubmit} id="checkout-form">
      <PaymentElement
        options={{
          layout: 'tabs',
          wallets: {
            link: 'never',
          },
        }}
      />
      {errorMessage && (
        <p
          className="mt-4 text-sm"
          style={{ color: '#dc2626', fontFamily: "'Bricolage Grotesque', sans-serif" }}
        >
          {errorMessage}
        </p>
      )}
    </form>
  );
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clientSecret, setClientSecret] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkoutReady, setCheckoutReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const planParam = searchParams.get('plan') || 'yearly';
  const isYearly = planParam === 'yearly';

  const features = [
    "Your personal recipe library",
    "Quick recipe import",
    "Shareable recipe links",
    "Custom filters & categories",
    "Public bio with social links",
  ];

  useEffect(() => {
    let cancelled = false;
    const createSubscription = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        if (!token) {
          navigate('/');
          return;
        }

        const subscriptionRes = await axios.get(`${API}/subscription`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (subscriptionRes.data.status === 'active') {
          toast.success("You're already on the Home Cook plan.");
          navigate('/recipes', { replace: true });
          return;
        }

        const configRes = await axios.get(`${API}/subscription/config`);
        const config = configRes.data;
        const plan = config.plans?.home_cook;
        const priceId = isYearly ? plan?.yearly_price_id : plan?.monthly_price_id;

        if (!priceId || !config.stripe_enabled) {
          setError("Payments are not available yet.");
          setIsLoading(false);
          return;
        }

        const paymentSetup = await setupSubscriptionPayment({
          priceId,
        });

        if (!cancelled) {
          setClientSecret(paymentSetup.client_secret);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Payment setup failed', {
            code: err.code || 'unknown',
            status: err.status || err.response?.status || null,
            name: err.name,
            message: err.message,
          });
          const msg = (
            err.userMessage
            || err.response?.data?.detail
            || "Could not set up payment"
          );
          setError(msg);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    createSubscription();
    return () => { cancelled = true; };
  }, [isYearly, navigate]);

  const elementsOptions = clientSecret ? {
    clientSecret,
    appearance: {
      theme: 'flat',
      variables: {
        colorPrimary: '#875C66',
        colorBackground: '#F3EEE4',
        colorText: '#51120A',
        colorDanger: '#dc2626',
        fontFamily: "'Bricolage Grotesque', sans-serif",
        borderRadius: '8px',
        spacingUnit: '4px',
      },
      rules: {
        '.Input': {
          backgroundColor: '#EEE7D8',
          border: '1px solid #D6D0C2',
          padding: '12px',
        },
        '.Input:focus': {
          border: '1px solid #51120A',
          boxShadow: 'none',
        },
        '.Label': {
          color: '#51120A',
          fontWeight: '500',
        },
        '.Tab': {
          backgroundColor: '#EEE7D8',
          border: '1px solid #D6D0C2',
        },
        '.Tab--selected': {
          backgroundColor: '#51120A',
          color: '#EEE7D8',
          border: '1px solid #51120A',
        },
      },
    },
  } : null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EEE7D8' }}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 mb-8 hover:opacity-70 transition-opacity"
          style={{
            color: '#875C66',
            fontFamily: "'Bricolage Grotesque', sans-serif",
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <h1
          className="text-3xl md:text-4xl mb-2"
          style={{
            fontFamily: "'Instrument Serif', serif",
            color: '#51120A',
            lineHeight: '1.1',
          }}
        >
          Complete your subscription
        </h1>
        <p
          className="text-sm mb-8"
          style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            color: '#51120A',
            opacity: 0.7,
          }}
        >
          You're subscribing to the Home Cook plan.
        </p>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
          <div>
            <h2
              className="text-lg font-bold mb-4"
              style={{
                fontFamily: "'Bricolage Grotesque', sans-serif",
                color: '#51120A',
              }}
            >
              Payment details
            </h2>

            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div
                  className="animate-spin rounded-full h-8 w-8 border-2"
                  style={{ borderColor: '#D6D0C2', borderTopColor: '#51120A' }}
                />
              </div>
            )}

            {error && (
              <div
                className="rounded-xl p-6 text-center"
                style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2' }}
              >
                <p
                  className="text-sm mb-4"
                  style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}
                >
                  {error}
                </p>
                <Button
                  onClick={() => navigate(-1)}
                  variant="outline"
                  style={{
                    borderColor: '#51120A',
                    color: '#51120A',
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                  }}
                >
                  Go Back
                </Button>
              </div>
            )}

            {clientSecret && elementsOptions && (
              <Elements stripe={stripePromise} options={elementsOptions}>
                <CheckoutForm
                  isYearly={isYearly}
                  onStatusChange={({ stripe, elements, isProcessing: p }) => {
                    setCheckoutReady(!!stripe && !!elements);
                    setIsProcessing(p);
                  }}
                />
              </Elements>
            )}
          </div>

          <div
            className="rounded-2xl px-6 pb-6"
            style={{
              backgroundColor: '#F3EEE4',
              border: '1px solid #D6D0C2',
              paddingTop: '1.2rem',
              marginTop: '2.25rem',
            }}
          >
            <div className="flex items-center justify-between mb-1">
              <h3
                className="text-lg font-bold"
                style={{
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  color: '#51120A',
                }}
              >
                Home Cook
              </h3>
              <div className="text-right">
                <span
                  className="text-2xl font-bold"
                  style={{
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    color: '#51120A',
                  }}
                >
                  ${isYearly ? '100' : '10'}
                </span>
                <span
                  className="text-sm ml-1"
                  style={{
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    color: '#51120A',
                  }}
                >
                  / {isYearly ? 'year' : 'month'}
                </span>
              </div>
            </div>

            {isYearly && (
              <p
                className="text-sm mb-4"
                style={{
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  color: '#51120A',
                }}
              >
                That's just $8.33/month — save $20 vs monthly
              </p>
            )}

            <ul className="space-y-2 mb-6">
              {features.map((feature, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check size={16} className="mt-0.5 flex-shrink-0" style={{ color: '#51120A' }} />
                  <span
                    className="text-sm"
                    style={{
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      color: '#51120A',
                    }}
                  >
                    {feature}
                  </span>
                </li>
              ))}
            </ul>

            <Button
              type="button"
              disabled={!checkoutReady || isProcessing || isLoading}
              onClick={() => {
                if (window.__checkoutSubmit) window.__checkoutSubmit();
              }}
              className="w-full py-6 text-base font-medium text-white transition-colors"
              style={{
                backgroundColor: '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif",
              }}
            >
              {isProcessing ? 'Processing...' : `Subscribe — $${isYearly ? '100/year' : '10/month'}`}
            </Button>
            <div className="flex items-center justify-center gap-2 mt-3">
              <Link size={12} style={{ color: '#51120A' }} />
              <p
                className="text-xs"
                style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
              >
                Secured by Stripe. Cancel anytime.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
